import { useCallback, useEffect, useMemo, useState } from "react";
import { SelectChangeEvent } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ActivationFormContext from "./activation-form-context";
import API from "../api/API";
import { ErrorViewButtonConfig } from "../types/types";

interface Props {
  children: React.ReactNode;
}

const ActivationFormProvider = ({ children }: Props) => {
  // Form states
  const [emailInputValue, setEmailInputValue] = useState("");
  const [emailInputError, setEmailInputError] = useState<string>();
  const [imeiInputValue, setImeiInputValue] = useState("");
  const [imeiInputError, setImeiInputError] = useState<string>();
  const [selectValue, setSelectValue] = useState("");
  const [selectValueError, setSelectValueError] = useState<string>();
  const [hearAboutUsValue, setHearAboutUsValue] = useState("");
  const [hearAboutUsError, setHearAboutUsError] = useState<string>();
  // Loading state
  const [loading, setLoading] = useState(false);

  // Error View states
  const [errorMessage, setErrorMessage] = useState<string>();
  const [errorEmail, setErrorEmail] = useState<string>();
  const [errorTitle, setErrorTitle] = useState<string>();
  const [errorButtonConfig, setErrorButtonConfig] = useState<ErrorViewButtonConfig>({ showButton: false });

  const disableSubmit = useMemo(
    () => selectValue === "" || hearAboutUsValue === "" || imeiInputValue.length !== 15,
    [imeiInputValue.length, selectValue, hearAboutUsValue]
  );

  const navigate = useNavigate();

  // To get rid of linter errors
  useEffect(() => {
    setEmailInputError(undefined);
    setImeiInputError(undefined);
    setErrorMessage(undefined);
    setLoading(false);
  }, []);

  const emailInputChangeHandler = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEmailInputError(undefined);
    setEmailInputValue(e.target.value);
  };

  const createShopifyCart = async (imei: string, email: string | null = null): Promise<any> => {
    const payload: any = {
      attributes: [
        { key: "DEVICE_IMEI", value: imei },
      ]
    };

    if (email) {
      payload.buyer_email = email;
      payload.attributes.push({
        key: "ACTIVATION_EMAIL",
        value: email
      });
    }

    const response = await API.post("/shopify/cart/create", payload, {
      headers: {
        'dd-log-msg': `[Activation Flow] Details - Cart session created for IMEI: ${imei}`
      }
    });

    return response.data;
  };

  const imeiInputChangeHandler = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setImeiInputError(undefined);

    const onlyNums = e.target.value.replace(/[^0-9]/g, "");
    if (onlyNums.length <= 15) {
      setImeiInputValue(onlyNums);
    }
  };

  const selectValueChangeHandler = (e: SelectChangeEvent<string>) => {
    setSelectValueError(undefined);
    setSelectValue(e.target.value.toString());
  };

  const hearAboutUsChangeHandler = (e: SelectChangeEvent<string>) => {
    setHearAboutUsError(undefined);
    setHearAboutUsValue(e.target.value.toString());
  };

  const qrDecodeHandler = (imei: string) => setImeiInputValue(imei);

  const imeiInputErrorChangeHandler = (error: string | undefined) => setImeiInputError(error);

  const emailInputErrorChangeHandler = (error: string | undefined) => setEmailInputError(error);

  const handleActivateNavigation = (confirmationNumber: string) => {
    const searchParams = new URLSearchParams({
      confirmation_number: confirmationNumber,
    });

    navigate(`/activating?${searchParams.toString()}`);
  };

  const formSubmitHandler = useCallback(() => {
    setLoading(true);
    API.post("/devices/activation/checkAndVerify", {
      // email: emailInputValue,
      imei: imeiInputValue,
      channel: selectValue,
      heard_from: hearAboutUsValue,
      referrer: "Web",
    }, { headers: {
      'dd-log-msg': `[Activation Flow] Details - Customer verifies IMEI: ${imeiInputValue}`
    }})
      .then(async (res) => {
        try {
          switch (res.data.status) {
            // Redirect to Shopify
            case 1: {
              const shopifyCartData = await createShopifyCart(imeiInputValue);
              const cartData = shopifyCartData?.cart;
              const clientId = localStorage.getItem("client_id");
              const sessionId = localStorage.getItem("session_id");
              const { channel } = res.data;
              window.location.href = `https://cosmotogether.com/pages/purchase-activation/?clientId=${clientId}&sessionId=${sessionId}&channel=${channel}&cartId=${btoa(cartData.id)}`;
              break;
            }
            // Show Error View with a few modifications and a button that redirects to /activating
            case 2: {
              const { confirmationNumber } = res.data;
              setErrorTitle("Start Activating your SIM");
              setErrorButtonConfig({
                showButton: true,
                text: "Click Here for Activation",
                onClick: () => handleActivateNavigation(confirmationNumber),
              });
              setErrorMessage(
                "Seems like you have paid but not activated the sim and left in-between. Click below link to activate"
              );
              console.info("Client has paid the but not activated the sim and left in-between", {
                apiRoute: "devices/verify",
                timeStamp: new Date().toISOString(),
              });
              break;
            }
            // Redirect to /activated - the device has already been activated
            case 3: {
              setImeiInputError("This IMEI has already been activated.");
              break;
            }
            // Show Activation Pending View
            case 6: {
              const { email } = res.data;
              setErrorEmail(email);
              setErrorMessage("Activation Pending");
              console.info("Activation Pending", { apiRoute: "devices/verify", timeStamp: new Date().toISOString() });
              break;
            }
            // Show Error View with default error
            default:
              setErrorMessage("Unkown Error");
              console.info("Unkown Error", { apiRoute: "devices/verify", timeStamp: new Date().toISOString() });
              break;
          }
        } catch (e) {
          // If anything goes wrong in the response handling, display unknown error
          setErrorMessage("Unkown Error");
        }
      })
      .catch((error) => {
        // In case no error response received
        if(!error.response) {
          setErrorMessage("We failed to retrieve information for your device. Please try again after some time.");
          console.error("Error calling device verify api", error, {
            apiRoute: "devices/verify",
            timeStamp: new Date().toISOString(),
          });
          return;
        }

        // In case of IMEI Length is Invalid, invalid email, invalid channel or IMEI not found, show the input error message instead of the Error View
        if (error.response.status === 400) {
          let errorFound = false;

          if (error.response.data.message.includes("email must be an email")) {
            setEmailInputError("Invalid email");
            errorFound = true;
          }
          if (error.response.data.message.includes("IMEI Length is Invalid")) {
            setImeiInputError("IMEI Length is Invalid");
            errorFound = true;
          }
          if (error.response.data.message.includes("channel must be a number between 1 and 6")) {
            setSelectValueError("Invalid channel");
            errorFound = true;
          }

          if (errorFound) {
            return;
          }
        }

        if (error.response.status === 404 && error.response.data.message === "003 | IMEI not found") {
          setImeiInputError("IMEI not found");
          return;
        }

        try {
          switch (error.response.data.status) {
            // Show Error View with a button that redirects to first page
            case 4:
              setErrorMessage(error.response.data.message);
              console.error("Error calling device verify api: Go to Scan Page", error, {
                apiRoute: "devices/verify",
                timeStamp: new Date().toISOString(),
              });
              break;
            // Show Error View with a special message instead of button
            case 7:
              setErrorButtonConfig({
                showButton: true,
                text: "Try another watch",
                href: "/",
              });
              setErrorMessage(
                `${error.response.data.message}`
              );
              console.error("Error calling device verify api: unknown error", error, {
                apiRoute: "devices/verify",
                timeStamp: new Date().toISOString(),
              });
              break;
            // Show Error View with response message
            default:
              setErrorMessage(error.response.data.message ?? error.response.message ?? "Unkown Error");
              console.error("Error calling device verify api", error, {
                apiRoute: "devices/verify",
                timeStamp: new Date().toISOString(),
              });
              break;
          }
        } catch (e) {
          // If anything goes wrong in the error handling, display Error View with unknown error
          console.error("Error calling device verify api", error, {
            apiRoute: "devices/verify",
            timeStamp: new Date().toISOString(),
          });
          setErrorMessage("Unkown Error");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    // emailInputValue,
    imeiInputValue,
    navigate,
    selectValue,
    hearAboutUsValue,
  ]);

  const activationFormContext = useMemo(
    () => ({
      emailInputValue,
      emailInputChangeHandler,
      emailInputError,
      emailInputErrorChangeHandler,
      imeiInputValue,
      imeiInputChangeHandler,
      imeiInputError,
      imeiInputErrorChangeHandler,
      selectValue,
      selectValueChangeHandler,
      selectValueError,
      hearAboutUsValue,
      hearAboutUsChangeHandler,
      hearAboutUsError,
      errorMessage,
      loading,
      formSubmitHandler,
      qrDecodeHandler,
      errorButtonConfig,
      errorEmail,
      errorTitle,
      disableSubmit,
    }),
    [
      emailInputError,
      emailInputValue,
      errorMessage,
      formSubmitHandler,
      imeiInputError,
      imeiInputValue,
      loading,
      selectValue,
      errorButtonConfig,
      errorEmail,
      errorTitle,
      selectValueError,
      disableSubmit,
      hearAboutUsValue,
      hearAboutUsError,
    ]
  );

  return <ActivationFormContext.Provider value={activationFormContext}>{children}</ActivationFormContext.Provider>;
};
export default ActivationFormProvider;
